import { Configuration, OrganizationsApi } from '@/api';
import { invalidateOrganizationListQuery } from '@/api/queries/organizationQueries';
import useGetOrganizationQuery from '@/api/queries/useGetOrganizationQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { Organization, OrganizationCreate } from '@/models/Organization';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import OrganizationForm from '@/modules/admin/components/OrganizationForm';
import { OrganizationListPath } from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const OrganizationEdit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { organizationId } = useParams();

  const { isLoading, organization, invalidateQuery } = useGetOrganizationQuery(organizationId);

  const organizationsApi = new OrganizationsApi(new Configuration(useAuth));

  function isOrganizationUpdate(org: OrganizationCreate | Organization): org is Organization {
    return 'id' in org;
  }

  const handleOnSubmit = async (organization: OrganizationCreate | Organization): Promise<void> => {
    if (!isOrganizationUpdate(organization)) {
      return;
    }

    try {
      await organizationsApi.updateOrganization(organization.id, organization);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Organization "${organization.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();

      navigate(OrganizationListPath);
    } catch (error) {
      console.error('Failed to update organization:', error);
    }
  };

  const handleOnDelete = async () => {
    if (!organization?.id) {
      return;
    }

    try {
      await organizationsApi.deleteOrganization(organization.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Organization "${organization.name}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateOrganizationListQuery(queryClient);

      navigate(OrganizationListPath);
    } catch (error) {
      console.error('Failed to delete organization:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!organization) {
    return;
  }

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Edit Organization">
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete Organization"
          message={`Are you sure you want to delete organization ${organization?.name}? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Organization
            </Button>
          )}
        </Confirm>
      </PageHeader>

      <div className="w-full bg-white mx-auto rounded-lg shadow p-6 lg:p-12">
        <OrganizationForm onSubmit={handleOnSubmit} organization={organization} />
      </div>
    </>
  );
};

export default OrganizationEdit;
